/**
 * 회원 자동 동기화 훅
 *
 * 컴포넌트 마운트 시 Studiomate 전체 회원 동기화(syncStudiomateMembers)를 실행하고,
 * 완료 후 Firestore에서 최신 회원 목록을 반환한다.
 *
 * - 동기식: 동기화 완료 전까지 isLoading=true
 * - 자동 실행: 마운트마다 재실행 (staleTime: 0, gcTime: 0)
 * - 에러 무시: 동기화 실패 시 기존 Firestore 데이터로 폴백
 */
import { useQuery } from "@tanstack/react-query";
import { triggerMemberSync } from "@/services/sync-members";
import { getMembers } from "@/services/firestore/members";
import type { Member } from "@vlab-corporation/vlab-pilates-service-interface/customer";

export function useSyncMembers(
  ownerUid: string,
  centerId: number,
): {
  membersMap: Map<string, Member> | undefined;
  isLoading: boolean;
} {
  const { data: membersMap, isLoading } = useQuery({
    queryKey: ["syncedMembers", ownerUid, centerId],
    queryFn: async (): Promise<Map<string, Member>> => {
      // 1. Studiomate 전체 회원 동기화 (실패해도 계속 진행)
      try {
        await triggerMemberSync();
      } catch {
        // 동기화 실패는 치명적이지 않음 — 기존 Firestore 데이터로 계속 동작
      }
      // 2. Firestore에서 최신 회원 목록 반환
      return getMembers(ownerUid, centerId);
    },
    staleTime: 0, // 매 마운트마다 재실행
    gcTime: 0, // 언마운트 즉시 캐시 삭제
    refetchOnWindowFocus: false,
    retry: 0,
    enabled: !!ownerUid && !!centerId,
  });

  return { membersMap, isLoading };
}
